/**
 * "How the load times are worked out" — opened by the ⓘ beside a P95.
 *
 * The page's opens drawn from fastest to slowest with the typical, P95 and
 * P99 marked, one sentence each, and the total time waited. With the real
 * opens (Page Detail, short periods) the bars ARE those opens; otherwise
 * they are an illustration drawn through the figures, and the window says
 * so rather than passing a picture off as data.
 */
import { Modal } from '../ui/Modal'
import {
  countOf,
  formatMs,
  formatWaited,
  opensPicture,
  pageNameForRoute,
} from '../../lib/page-perf/view-model'
import { STATUS_TONE } from './perf-tones'

const MARK_TONE = {
  p50: STATUS_TONE.healthy,
  p95: STATUS_TONE.slow,
  p99: STATUS_TONE.critical,
}

function Swatch({ tone }) {
  return <span aria-hidden="true" className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: tone }} />
}

/**
 * `page` = { route, loads, users, avgMs, p50Ms, p95Ms, p99Ms, waitedMs, opens? }
 * `scopeText` = e.g. "Today" — the period these figures cover.
 */
export function LoadTimeHelpModal({ open, onClose, page, scopeText, usersExact = false }) {
  if (!open || !page) return null
  const { route, loads, users, avgMs, p50Ms, p95Ms, p99Ms } = page
  const waitedMs = page.waitedMs ?? (Number.isFinite(avgMs) && Number.isFinite(loads) ? avgMs * loads : null)
  const pic = opensPicture({ opens: page.opens, loads, p50Ms, p95Ms, p99Ms })
  const max = Math.max(1, ...pic.bars)
  const n = Number.isFinite(loads) ? loads : 0
  const people = Number.isFinite(users) && users > 0 ? users : null

  const markOf = (i) =>
    i === pic.marks.p99 ? 'p99' : i === pic.marks.p95 ? 'p95' : i === pic.marks.p50 ? 'p50' : null
  const markLabel = { p50: `typical ${formatMs(p50Ms)}`, p95: `P95 ${formatMs(p95Ms)}`, p99: `P99 ${formatMs(p99Ms)}` }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop
      title={`${pageNameForRoute(route)} — how the load times are worked out`}
      description={`${scopeText} · ${countOf(n, 'open')}${people ? ` by ${countOf(people, 'person', 'people')}${usersExact ? '' : ' (counted once per day)'}` : ''} · each open has its own stopwatch time, from the click until the page is on screen and usable`}
    >
      <div className="min-w-0">
        <div
          aria-hidden="true"
          className="relative flex h-40 items-end gap-[2px] border-b border-[color:var(--color-rule-strong)] pt-6"
        >
          {pic.bars.map((v, i) => {
            const mark = markOf(i)
            const pct = Math.max(2, (v / max) * 100)
            return (
              <div key={i} className="relative flex h-full min-w-0 flex-1 items-end">
                <div
                  className="w-full rounded-t-sm"
                  style={{
                    height: `${pct}%`,
                    backgroundColor: mark ? MARK_TONE[mark] : 'var(--color-accent)',
                    opacity: mark ? 1 : 0.35,
                  }}
                />
                {mark ? (
                  <span
                    className={
                      'absolute whitespace-nowrap text-[10px] font-semibold text-[color:var(--color-ink-strong)] ' +
                      // Marks near the right edge hang leftwards so the label
                      // never runs past the window (no sideways scroll).
                      (i > pic.bars.length * 0.7 ? 'right-0' : '-translate-x-1/2 left-1/2')
                    }
                    style={{
                      bottom: `calc(${pct}% + ${mark === 'p95' ? 14 : 2}px)`,
                    }}
                  >
                    {markLabel[mark]}
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
        <p className="mt-1 flex justify-between gap-2 text-[11px] text-[color:var(--color-ink-subtle)]">
          <span>fastest open</span>
          <span className="text-center">the opens, sorted from fastest to slowest</span>
          <span>slowest open</span>
        </p>

        <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[190px_1fr]">
          <dt className="font-semibold text-[color:var(--color-ink-muted)]"><Swatch tone={MARK_TONE.p50} />Typical open (P50)</dt>
          <dd className="m-0"><b>{formatMs(p50Ms)}</b> — the open in the middle: half of the {countOf(n, 'open')} were faster than this, half slower. What most people feel.</dd>
          <dt className="font-semibold text-[color:var(--color-ink-muted)]"><Swatch tone={MARK_TONE.p95} />Slowest 5 of 100 (P95)</dt>
          <dd className="m-0"><b>{formatMs(p95Ms)}</b> — 95 of every 100 opens finish within this; only the slowest 5 take longer. <b>The status colour uses this figure</b>, so one very slow open can change the colour of the row when there are few opens.</dd>
          <dt className="font-semibold text-[color:var(--color-ink-muted)]"><Swatch tone={MARK_TONE.p99} />Slowest 1 of 100 (P99)</dt>
          <dd className="m-0"><b>{formatMs(p99Ms)}</b> — 99 of 100 finish within this; the worst experience anyone had.</dd>
          <dt className="font-semibold text-[color:var(--color-ink-muted)]"><Swatch tone="var(--color-accent)" />Average open</dt>
          <dd className="m-0"><b>{formatMs(avgMs)}</b> — all {countOf(n, 'time')} added up and divided by {n}. Slow opens pull it up; that is why it can be higher than the typical open.</dd>
        </dl>

        <p className="mt-4 rounded-lg bg-[color:var(--color-canvas)] px-3 py-2 text-sm">
          ⏱ <b>Time waited in total: {formatWaited(waitedMs)}</b> — the {countOf(n, 'open')} added together (average {formatMs(avgMs)} × {n})
          {people ? `. With ${countOf(people, 'person', 'people')}, about ${formatWaited(waitedMs / people)} of waiting per person.` : '.'}
        </p>
        <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">
          {pic.real
            ? page.opensExact === false
              ? 'More than 200 opens: the bars are read off the stored distribution of this period’s opens, evenly spaced from fastest to slowest.'
              : `Each bar is one real open in this period${pic.bars.length < n ? ` (${countOf(pic.bars.length, 'open')} shown, evenly sampled)` : ''}.`
            : 'The bars are an illustration drawn from these figures — open the page for its real timeline.'}
        </p>
      </div>
    </Modal>
  )
}
